import { ShowPasswordInputIcon } from '@unistocks/components/basic/input';
import { Form } from '@unistocks/components/form/form';
import { Page } from '@unistocks/components/page/page';
import { Paragraph } from '@unistocks/components/text/text';
import { AuthContainer } from '@unistocks/features/authentication/auth-container';
import { t } from '@unistocks/shared/localization/index.ts';
import { setAccountResetPasswordRequestConfig } from '@unistocks/proxy/axios-client/AccountClient';
import { useAccountResetPasswordMutation } from '@unistocks/proxy/axios-client/AccountQuery';
import { TENANT_HEADER } from '@unistocks/shared/consts';
import { style } from '@unistocks/shared/styles';
import { usePasswordValidationSchema } from '@unistocks/shared/utils/password-validator';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { useMedia } from 'tamagui';

type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

type ResetPasswordProps = {
  tenantId?: string;
  userId: string;
  resetToken: string;
};

export const ResetPassword: React.FC<ResetPasswordProps> = ({ tenantId, userId, resetToken }) => {
  const router = useRouter();
  const media = useMedia();
  const [showPassword, setShowPassword] = useState(false);
  const { data: schema } = usePasswordValidationSchema({ withConfirmField: true });

  const { mutateAsync: accountResetPassword } = useAccountResetPasswordMutation({
    onSuccess: () => {
      router.dismissTo('/(auth)/login');
    }
  });

  const submit = ({ password }: ResetPasswordFormData) => {
    setAccountResetPasswordRequestConfig({
      headers: { [TENANT_HEADER]: tenantId }
    });
    return accountResetPassword({
      password,
      userId,
      resetToken
    });
  };

  return (
    <Page.ScrollView
      headerTransparent
      headerLogo={false}
      headerTitle={media.sm ? undefined : t('AbpAccount::ResetPassword')}
      contentContainerStyle={media.sm ? undefined : style.centerVertical}
    >
      <AuthContainer title={t('AbpAccount::ResetPassword')}>
        <Paragraph size={'$5'} textAlign={'center'}>
          {t('AbpAccount::ResetPassword_Information')}
        </Paragraph>

        <Form schema={schema} onSubmit={submit}>
          <Form.Input
            name={'password'}
            label={t('AbpAccount::Password')}
            type={showPassword ? 'text' : 'password'}
            textContentType={'password'}
            autoCapitalize={'none'}
            iconAfter={
              <ShowPasswordInputIcon showPassword={showPassword} setShowPassword={setShowPassword} />
            }
          />

          <Form.Input
            name={'confirmPassword'}
            label={t('AbpAccount::ConfirmPassword')}
            type={showPassword ? 'text' : 'password'}
            textContentType={'password'}
            autoCapitalize={'none'}
            returnKeyType={'send'}
            iconAfter={
              <ShowPasswordInputIcon showPassword={showPassword} setShowPassword={setShowPassword} />
            }
          />
        </Form>
      </AuthContainer>
    </Page.ScrollView>
  );
};

ResetPassword.displayName = 'ResetPassword';
